import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  KeyIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  TrashIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  CommandLineIcon,
  ArrowTopRightOnSquareIcon,
  LockClosedIcon,
  ChatBubbleLeftRightIcon,
  CodeBracketIcon,
  BookOpenIcon,
  ServerStackIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { useToast, ToastContainer } from '../components/Toast';
import { useEmpresa } from '../contexts/EmpresaContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { integracoesService } from '../services/integracoes.service';
import { copyToClipboard } from '../utils/clipboard';
import { formatCNPJ, formatPhone } from '../utils/formatters';
import { formatDateTimeBR } from '../utils/dateUtils';
import {
  getSwaggerURL,
  getIntegrationApiBaseURL,
  getApiHostURL,
} from '../utils/apiConfig';
import type {
  CreateClientApiCredentialPayloadContract,
  EmpresaIntegracaoCredencialContract,
} from '@sstexames/shared-types';

type ActiveTab = 'credenciais' | 'tutoriais' | 'ambientes';
type CodeLanguage = 'curl' | 'node' | 'python';

const buildSnippets = (apiBaseUrl: string) => ({
  dominios: {
    curl: `curl -X GET "${apiBaseUrl}/dominios" \\\n  -H "X-API-Key: sst_live_sua_chave_aqui" \\\n  -H "Content-Type: application/json"`,
    node: `const response = await fetch('${apiBaseUrl}/dominios', {\n  headers: {\n    'X-API-Key': 'sst_live_sua_chave_aqui',\n    'Content-Type': 'application/json',\n  },\n});\nconst data = await response.json();\nconsole.log(data);`,
    python: `import requests\n\nurl = "${apiBaseUrl}/dominios"\nheaders = {\n    "X-API-Key": "sst_live_sua_chave_aqui",\n    "Content-Type": "application/json"\n}\nresponse = requests.get(url, headers=headers)\nprint(response.json())`,
  },
  colaboradores: {
    curl: `curl -X POST "${apiBaseUrl}/colaboradores" \\\n  -H "X-API-Key: sst_live_sua_chave_aqui" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "nome": "Fernando Henrique de Souza",\n    "cpf": "12345678901",\n    "data_admissao": "2026-10-15",\n    "cargo_id": 679,\n    "setor_id": 609,\n    "email": "fernando.souza@empresa.com.br",\n    "telefone": "11999998888",\n    "actor": {\n      "id": "USR-9482",\n      "nome": "Carlos Silva (Operador RH)",\n      "email": "carlos.silva@empresa.com.br"\n    }\n  }'`,
    node: `const response = await fetch('${apiBaseUrl}/colaboradores', {\n  method: 'POST',\n  headers: {\n    'X-API-Key': 'sst_live_sua_chave_aqui',\n    'Content-Type': 'application/json',\n  },\n  body: JSON.stringify({\n    nome: 'Fernando Henrique de Souza',\n    cpf: '12345678901',\n    data_admissao: '2026-10-15',\n    cargo_id: 679,\n    setor_id: 609,\n    email: 'fernando.souza@empresa.com.br',\n    telefone: '11999998888',\n    actor: {\n      id: 'USR-9482',\n      nome: 'Carlos Silva (Operador RH)',\n      email: 'carlos.silva@empresa.com.br',\n    },\n  }),\n});\nconst result = await response.json();\nconsole.log(result);`,
    python: `import requests\n\npayload = {\n    "nome": "Fernando Henrique de Souza",\n    "cpf": "12345678901",\n    "data_admissao": "2026-10-15",\n    "cargo_id": 679,\n    "setor_id": 609,\n    "email": "fernando.souza@empresa.com.br",\n    "telefone": "11999998888",\n    "actor": {\n        "id": "USR-9482",\n        "nome": "Carlos Silva (Operador RH)",\n        "email": "carlos.silva@empresa.com.br"\n    }\n}\nresponse = requests.post(\n    "${apiBaseUrl}/colaboradores",\n    json=payload,\n    headers={"X-API-Key": "sst_live_sua_chave_aqui"}\n)\nprint(response.json())`,
  },
  agendamento: {
    curl: `curl -X POST "${apiBaseUrl}/agendamentos/solicitar-link-whatsapp" \\\n  -H "X-API-Key: sst_live_sua_chave_aqui" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "cpf": "12345678901",\n    "tipo_exame": "periodico",\n    "telefone_whatsapp": "11999998888",\n    "notificar_whatsapp": true,\n    "observacao": "Agendamento periódico solicitado via integração ERP"\n  }'`,
    node: `const response = await fetch('${apiBaseUrl}/agendamentos/solicitar-link-whatsapp', {\n  method: 'POST',\n  headers: {\n    'X-API-Key': 'sst_live_sua_chave_aqui',\n    'Content-Type': 'application/json',\n  },\n  body: JSON.stringify({\n    cpf: '12345678901',\n    tipo_exame: 'periodico',\n    telefone_whatsapp: '11999998888',\n    notificar_whatsapp: true,\n    observacao: 'Agendamento periódico solicitado via integração ERP',\n  }),\n});\nconst data = await response.json();\nconsole.log('Link enviado ao colaborador:', data.link_agendamento);`,
    python: `import requests\n\npayload = {\n    "cpf": "12345678901",\n    "tipo_exame": "periodico",\n    "telefone_whatsapp": "11999998888",\n    "notificar_whatsapp": True,\n    "observacao": "Agendamento via integração ERP"\n}\nresponse = requests.post(\n    "${apiBaseUrl}/agendamentos/solicitar-link-whatsapp",\n    json=payload,\n    headers={"X-API-Key": "sst_live_sua_chave_aqui"}\n)\nprint(response.json())`,
  },
});

export default function Integracoes() {
  const { empresa, isIntegracoes } = useEmpresa();
  const { hasModulo, isLoading: isFeaturesLoading } = useFeatureAccess();
  const queryClient = useQueryClient();
  const { toasts, removeToast, success, error, warning } = useToast();

  const [activeTab, setActiveTab] = useState<ActiveTab>('credenciais');
  const [selectedLanguage, setSelectedLanguage] = useState<CodeLanguage>('curl');
  const [copiedSnippetId, setCopiedSnippetId] = useState<string | null>(null);

  // URLs dinâmicas calculadas conforme variáveis de ambiente (.env)
  const apiBaseUrl = getIntegrationApiBaseURL();
  const swaggerUrl = getSwaggerURL();
  const apiHost = getApiHostURL();
  const isStaging = apiHost.includes('staging') || apiHost.includes('sstlocal') || apiHost.includes('localhost');
  const isProd = !isStaging;
  const snippets = React.useMemo(() => buildSnippets(apiBaseUrl), [apiBaseUrl]);

  // Estado para edição de IPs da empresa
  const [allowedIpsOverride, setAllowedIpsOverride] = useState<string | null>(null);

  // Modais de Chave de API
  const [isCreateKeyModalOpen, setIsCreateKeyModalOpen] = useState(false);
  const [keyCreatedSecret, setKeyCreatedSecret] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Formulário de criação de chave
  const [newKeyForm, setNewKeyForm] = useState<{
    nome: string;
    allowed_ips: string;
    allowed_modulos: string[];
    expires_at: string;
  }>({
    nome: '',
    allowed_ips: '',
    allowed_modulos: [],
    expires_at: '',
  });

  // Modal de Revogação
  const [keyToRevoke, setKeyToRevoke] = useState<{ id: number; nome: string } | null>(null);
  const [selectedSharedCred, setSelectedSharedCred] = useState<EmpresaIntegracaoCredencialContract | null>(null);

  // Consulta de dados de integração da empresa
  const {
    data: integracao,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['customer-integracao', empresa?.id],
    queryFn: () => integracoesService.getIntegracao(),
    enabled: !!empresa?.id && isIntegracoes && hasModulo('integracoes'),
  });

  const allowedIpsInput =
    allowedIpsOverride !== null ? allowedIpsOverride : integracao?.api_allowed_ips || '';
  const hasIpsChanged =
    allowedIpsOverride !== null && allowedIpsOverride !== (integracao?.api_allowed_ips || '');

  // Mutação para atualizar IPs da empresa
  const updateIpsMutation = useMutation({
    mutationFn: (ips: string) =>
      integracoesService.updateIps({
        api_allowed_ips: ips,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-integracao', empresa?.id] });
      success('Lista de IPs autorizados da empresa atualizada com sucesso!');
      setAllowedIpsOverride(null);
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      error(
        err.response?.data?.message || 'Erro ao atualizar regras de IP da empresa.',
      );
    },
  });

  // Redirecionamento de Comunicação (Staging / Homologação)
  const [emailRedirectOverride, setEmailRedirectOverride] = useState<string | null>(null);
  const [whatsappRedirectOverride, setWhatsappRedirectOverride] = useState<string | null>(null);

  const emailRedirectInput =
    emailRedirectOverride !== null
      ? emailRedirectOverride
      : integracao?.email_redirecionamento || '';
  const whatsappRedirectInput =
    whatsappRedirectOverride !== null
      ? whatsappRedirectOverride
      : integracao?.whatsapp_redirecionamento || '';

  const hasRedirectChanged =
    (emailRedirectOverride !== null &&
      emailRedirectOverride !== (integracao?.email_redirecionamento || '')) ||
    (whatsappRedirectOverride !== null &&
      whatsappRedirectOverride !== (integracao?.whatsapp_redirecionamento || ''));

  const updateRedirectMutation = useMutation({
    mutationFn: (payload: { email_redirecionamento?: string; whatsapp_redirecionamento?: string }) =>
      integracoesService.updateRedirecionamento(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-integracao', empresa?.id] });
      success('Configurações de redirecionamento atualizadas com sucesso!');
      setEmailRedirectOverride(null);
      setWhatsappRedirectOverride(null);
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      error(
        err.response?.data?.message || 'Erro ao atualizar redirecionamento de comunicação.',
      );
    },
  });

  const handleSaveRedirect = (e: React.FormEvent) => {
    e.preventDefault();
    updateRedirectMutation.mutate({
      email_redirecionamento: emailRedirectInput.trim(),
      whatsapp_redirecionamento: whatsappRedirectInput.trim(),
    });
  };

  // Mutação para criar nova chave de API
  const createKeyMutation = useMutation({
    mutationFn: (payload: CreateClientApiCredentialPayloadContract) =>
      integracoesService.createCredencial(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-integracao', empresa?.id] });
      setKeyCreatedSecret(data.api_key);
      success('Chave de API criada com sucesso!');
      setNewKeyForm({
        nome: '',
        allowed_ips: '',
        allowed_modulos: [],
        expires_at: '',
      });
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      error(
        err.response?.data?.message || 'Não foi possível gerar a chave de API.',
      );
    },
  });

  // Mutação para revogar chave de API
  const revokeKeyMutation = useMutation({
    mutationFn: (credencialId: number) =>
      integracoesService.revokeCredencial(credencialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-integracao', empresa?.id] });
      success('Chave de API revogada com sucesso.');
      setKeyToRevoke(null);
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      error(
        err.response?.data?.message || 'Erro ao revogar a credencial.',
      );
    },
  });

  const handleOpenCreateKey = () => {
    const defaultModulos = (integracao?.modulos_disponiveis || [])
      .filter((m) => m.contratado && m.chave !== 'integracoes')
      .map((m) => m.chave);

    setNewKeyForm({
      nome: '',
      allowed_ips: '',
      allowed_modulos: defaultModulos,
      expires_at: '',
    });
    setKeyCreatedSecret(null);
    setKeyCopied(false);
    setIsCreateKeyModalOpen(true);
  };

  const handleToggleModulo = (chave: string) => {
    const current = newKeyForm.allowed_modulos;
    if (current.includes(chave)) {
      setNewKeyForm({
        ...newKeyForm,
        allowed_modulos: current.filter((c) => c !== chave),
      });
    } else {
      setNewKeyForm({
        ...newKeyForm,
        allowed_modulos: [...current, chave],
      });
    }
  };

  const handleCreateKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyForm.nome.trim()) {
      warning('Informe um nome identificador para a integração.');
      return;
    }
    if (newKeyForm.allowed_modulos.length === 0) {
      warning('Selecione ao menos um módulo permitido para esta chave.');
      return;
    }

    createKeyMutation.mutate({
      nome: newKeyForm.nome.trim(),
      allowed_ips: newKeyForm.allowed_ips.trim() || undefined,
      allowed_modulos: newKeyForm.allowed_modulos,
      expires_at: newKeyForm.expires_at || undefined,
    });
  };

  const handleSaveIps = (e: React.FormEvent) => {
    e.preventDefault();
    updateIpsMutation.mutate(allowedIpsInput.trim());
  };

  const handleCopySecret = async () => {
    if (!keyCreatedSecret) return;
    const ok = await copyToClipboard(keyCreatedSecret);
    if (ok) {
      setKeyCopied(true);
      success('Token de API copiado para a área de transferência!');
      setTimeout(() => setKeyCopied(false), 3000);
    } else {
      warning('Não foi possível copiar automaticamente. Selecione e copie o texto manualmente.');
    }
  };

  const handleCopySnippet = async (id: string, code: string) => {
    const ok = await copyToClipboard(code);
    if (ok) {
      setCopiedSnippetId(id);
      success('Código copiado para a área de transferência!');
      setTimeout(() => setCopiedSnippetId(null), 2500);
    }
  };

  // Se não for perfil Integrações, exibe tela de acesso restrito
  if (!isIntegracoes) {
    return (
      <PageContainer>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-primary-50 rounded-2xl flex items-center justify-center text-primary-600 shadow-sm border border-primary-200">
            <LockClosedIcon className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Acesso Restrito ao Perfil Integrações
          </h2>
          <p className="text-gray-600 mb-6">
            O gerenciamento de chaves de integração e APIs da empresa é exclusivo para usuários com perfil de Integrações.
          </p>
          <a
            href="/"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition shadow-sm"
          >
            Voltar ao Início
          </a>
        </div>
      </PageContainer>
    );
  }

  // Se o módulo não estiver ativo no contrato da empresa ou grupo econômico
  if (!isFeaturesLoading && !hasModulo('integracoes')) {
    return (
      <PageContainer>
        <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 shadow-sm border border-amber-200">
              <LockClosedIcon className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Módulo de Integrações & API Não Contratado
            </h2>
            <p className="text-gray-600 mb-5">
              O módulo de Integrações & API REST não está habilitado no contrato de prestação de serviços desta empresa ou do seu grupo econômico.
              Entre em contato com nossa equipe comercial ou suporte para incluir este módulo ao seu plano.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href={swaggerUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-xl transition border border-primary-200 shadow-sm"
              >
                <BookOpenIcon className="w-4 h-4" />
                <span>Explorar Documentação Interativa (Swagger)</span>
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 opacity-70" />
              </a>
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-xl transition shadow-sm"
              >
                Voltar ao Início
              </a>
            </div>
          </div>

          {/* Vitrine de Integrações Prontas */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-2 text-primary-700 font-semibold text-xs uppercase tracking-wider">
              <CommandLineIcon className="w-4 h-4" />
              <span>Conheça os Recursos Prontos para Uso</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Integre o SST Exames diretamente ao ecossistema da sua empresa
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Nossa API RESTful foi construída para eliminar retrabalho manual, conectando seu RH, folha de pagamento e processos de saúde ocupacional.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100">
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    Agendamento via WhatsApp
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Dispare links interativos direto no WhatsApp do colaborador para que ele mesmo escolha a clínica credenciada mais próxima, dia e horário.
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
                  <BuildingOffice2Icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    Sincronização de RH & Folha
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Cadastre e atualize colaboradores, cargos e setores automaticamente integrados ao seu ERP (Senior, TOTVS, SAP e outros).
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0 border border-purple-100">
                  <CodeBracketIcon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    Tabelas de Domínios
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Acesse listas de filiais, cargos, setores e catálogos ocupacionais sempre atualizados via endpoints REST padronizados.
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100">
                  <ShieldCheckIcon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    Segurança e Auditoria
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Controle de acesso por API Key, Whitelist de IPs por empresa ou grupo econômico e logs de auditoria em conformidade com a LGPD.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-gray-500 text-center sm:text-left">
                Deseja desbloquear as integrações para sua empresa? Fale com a nossa equipe de atendimento.
              </p>
              <a
                href="https://wa.me/5511999999999?text=Ol%C3%A1%2C%20gostaria%20de%20solicitar%20a%20habilita%C3%A7%C3%A3o%20do%20m%C3%B3dulo%20de%20Integra%C3%A7%C3%B5es%20%26%20API%20para%20minha%20empresa."
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition shadow-sm shrink-0"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                <span>Solicitar Habilitação com Consultor</span>
              </a>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (isLoading || (!integracao && !queryError)) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center py-28">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            <p className="text-sm text-gray-500 font-medium">Carregando configurações de integração...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (queryError || !integracao) {
    return (
      <PageContainer>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-600 border border-rose-200">
            <ExclamationTriangleIcon className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Não foi possível carregar os dados de integração
          </h2>
          <p className="text-gray-600 mb-6">
            {(queryError as { response?: { data?: { message?: string } } })?.response?.data?.message ||
              'Ocorreu um erro ao consultar as informações de API da empresa.'}
          </p>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['customer-integracao'] })}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition shadow-sm"
          >
            Tentar Novamente
          </button>
        </div>
      </PageContainer>
    );
  }

  const isImplantada = integracao.is_implantada;
  const credenciais = integracao.credenciais || [];
  const modulosDisponiveis = (integracao.modulos_disponiveis || []).filter(
    (m) => m.chave !== 'integracoes',
  );

  return (
    <PageContainer>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <PageHeader
        title="Integrações & API REST"
        description="Emissão de chaves de API, controle de segurança por IP, tutoriais de sincronização e agendamento via link do WhatsApp."
        badge={
          isImplantada ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
              <CheckCircleIcon className="w-4 h-4 text-primary-600" />
              Implantada & Habilitada
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
              <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
              Em Implantação
            </span>
          )
        }
        actions={
          <div className="flex items-center gap-2.5">
            <a
              href={swaggerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200/80 rounded-xl shadow-sm hover:bg-gray-50 hover:text-primary-600 transition cursor-pointer"
            >
              <CommandLineIcon className="w-4 h-4 text-primary-600" />
              Swagger / Docs da API
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 ml-0.5 text-gray-400" />
            </a>
            <button
              onClick={handleOpenCreateKey}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl shadow-sm transition bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
            >
              <PlusIcon className="w-4 h-4" />
              Nova Chave de API
            </button>
          </div>
        }
      />

      {/* Banner Informativo do Endpoint da API */}
      <div className="mb-6 p-4 rounded-2xl border border-primary-200 bg-primary-50/70 flex items-start gap-3 shadow-sm">
        <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center shrink-0 text-primary-700 mt-0.5">
          <ServerStackIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 text-xs text-gray-700 leading-relaxed">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-bold text-gray-900 text-sm">
              Endpoint Ativo da API de Integração: {apiBaseUrl}
            </span>
            <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-primary-600 text-white uppercase tracking-wider">
              {isStaging ? 'Ambiente Staging (Homologação)' : 'Ambiente Produção'}
            </span>
          </div>
          <p className="text-gray-600">
            {isStaging ? (
              <>
                Ambiente Staging / Homologação (<code className="font-mono font-semibold text-primary-800 bg-white px-1.5 py-0.5 rounded border border-primary-200">{apiBaseUrl}</code>). Utilize exclusivamente este endpoint para homologação e testes de integração com seu ERP antes de ir para produção.
              </>
            ) : (
              <>
                Ambiente Produção (<code className="font-mono font-semibold text-primary-800 bg-white px-1.5 py-0.5 rounded border border-primary-200">https://api.sstexames.com.br/api/v1</code>). Destinado estritamente a operações oficiais com agendamentos e faturamentos reais.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Abas Superiores */}
      <div className="flex items-center space-x-1 border-b border-gray-200/80 mb-6">
        <button
          onClick={() => setActiveTab('credenciais')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
            activeTab === 'credenciais'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <KeyIcon className="w-4 h-4" />
          Credenciais & Segurança
          <span className="ml-1.5 px-2 py-0.5 text-[10px] font-bold rounded-full bg-gray-100 text-gray-600">
            {credenciais.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('tutoriais')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
            activeTab === 'tutoriais'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BookOpenIcon className="w-4 h-4" />
          Tutoriais & Exemplos da API
          <span className="ml-1.5 px-2 py-0.5 text-[10px] font-bold rounded-full bg-primary-100 text-primary-700">
            Guia
          </span>
        </button>

        <button
          onClick={() => setActiveTab('ambientes')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
            activeTab === 'ambientes'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <ServerStackIcon className="w-4 h-4" />
          Staging vs Produção
        </button>
      </div>

      {/* ==================== TAB 1: CREDENCIAIS & SEGURANÇA ==================== */}
      {activeTab === 'credenciais' && (
        <div className="space-y-6">


          {/* Banner Grupo Econômico se aplicável */}
          {integracao.grupo_economico && (
            <div className="p-4 rounded-2xl border border-indigo-200 bg-indigo-50/70 flex items-start gap-3 shadow-sm">
              <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0 text-indigo-700 mt-0.5">
                <BuildingOffice2Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 text-xs text-indigo-950 leading-relaxed">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-indigo-950 text-sm">
                    Empresa Vinculada ao Grupo Econômico: {integracao.grupo_economico.nome}
                  </span>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-indigo-600 text-white uppercase tracking-wider">
                    Grupo
                  </span>
                </div>
                <p className="text-indigo-900">
                  Esta empresa herda e compartilha credenciais de API geradas no escopo do seu Grupo Econômico.
                  Para chamadas de escrita (<code className="font-mono font-bold bg-indigo-100 px-1 py-0.5 rounded text-indigo-950">POST, PUT, DELETE</code>),
                  o envio do cabeçalho <code className="font-mono font-bold bg-indigo-100 px-1 py-0.5 rounded text-indigo-950">X-Empresa-ID: {integracao.empresa_id}</code> é obrigatório para direcionar as operações à empresa correta.
                  Em consultas (<code className="font-mono font-bold bg-indigo-100 px-1 py-0.5 rounded text-indigo-950">GET</code>), ele é opcional para permitir a listagem unificada de dados de todas as empresas do grupo.
                </p>
              </div>
            </div>
          )}

          {/* Card: Redirecionamento de Comunicação (Homologação & Testes) */}
          {integracao.redirecionamento_comunicacao_habilitado && (
            <div className="bg-amber-50/70 rounded-2xl shadow-sm border border-amber-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 text-amber-700 mt-0.5">
                    <ChatBubbleLeftRightIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-bold text-amber-950 uppercase tracking-wider">
                        Redirecionamento de Comunicação (E-mail & WhatsApp de Testes)
                      </h2>
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-amber-600 text-white uppercase tracking-wider">
                        Ambiente de Testes
                      </span>
                    </div>
                    <p className="text-xs text-amber-900 mt-1 leading-relaxed">
                      Configure aqui um e-mail e um número de WhatsApp de teste para a sua empresa. Enquanto preenchidos, qualquer notificação disparada por ações no portal ou via API (convites de autoagendamento, confirmações, lembretes de presença e avisos de cancelamento) será direcionada para esses contatos de teste, garantindo total autonomia para validar fluxos sem notificar colaboradores reais.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSaveRedirect} className="mt-4 pt-4 border-t border-amber-200/80">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="client_redirect_email" className="block text-xs font-semibold text-amber-950 mb-1">
                      E-mail de Teste
                    </label>
                    <input
                      id="client_redirect_email"
                      type="email"
                      value={emailRedirectInput}
                      onChange={(e) => setEmailRedirectOverride(e.target.value)}
                      placeholder="Ex: homologacao@suaempresa.com.br"
                      className="w-full text-xs rounded-xl border-amber-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 p-2.5 bg-white text-gray-900"
                    />
                    <p className="text-[11px] text-amber-800/80 mt-1">
                      Deixe vazio para não redirecionar e-mails.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="client_redirect_whatsapp" className="block text-xs font-semibold text-amber-950 mb-1">
                      WhatsApp de Teste
                    </label>
                    <input
                      id="client_redirect_whatsapp"
                      type="text"
                      value={whatsappRedirectInput}
                      onChange={(e) => setWhatsappRedirectOverride(formatPhone(e.target.value))}
                      placeholder="Ex: (11) 99999-9999"
                      className="w-full text-xs rounded-xl border-amber-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 p-2.5 bg-white text-gray-900"
                    />
                    <p className="text-[11px] text-amber-800/80 mt-1">
                      Deixe vazio para não redirecionar mensagens de WhatsApp.
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between pt-2">
                  <span className="text-xs text-amber-800">
                    {hasRedirectChanged
                      ? 'Alterações de redirecionamento não salvas'
                      : (emailRedirectInput || whatsappRedirectInput)
                      ? 'Redirecionamento de teste ativo para esta empresa'
                      : 'Nenhum redirecionamento configurado'}
                  </span>
                  <div className="flex items-center gap-2">
                    {hasRedirectChanged && (
                      <button
                        type="button"
                        onClick={() => {
                          setEmailRedirectOverride(null);
                          setWhatsappRedirectOverride(null);
                        }}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={!hasRedirectChanged || updateRedirectMutation.isPending}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-xl shadow-sm transition ${
                        hasRedirectChanged && !updateRedirectMutation.isPending
                          ? 'bg-amber-600 text-white hover:bg-amber-700 cursor-pointer'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {updateRedirectMutation.isPending ? 'Salvando...' : 'Salvar Redirecionamento'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Grid: Identificação da Empresa & Whitelist de IPs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cartão de Identificação da Empresa */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-5 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <BuildingOffice2Icon className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Empresa Vinculada
                  </h2>
                </div>
                <p className="text-base font-bold text-gray-900 leading-snug">{integracao.nome}</p>
                {integracao.razao_social && integracao.razao_social !== integracao.nome && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{integracao.razao_social}</p>
                )}

                <div className="mt-4 space-y-2 border-t border-gray-100 pt-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">CNPJ:</span>
                    <span className="font-semibold text-gray-800 font-mono">
                      {formatCNPJ(integracao.cnpj)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">ID da Empresa:</span>
                    <span className="font-bold text-primary-700 font-mono">#{integracao.empresa_id}</span>
                  </div>
                  {integracao.grupo_economico && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Grupo Econômico:</span>
                      <span className="font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded text-[11px] border border-indigo-200">
                        {integracao.grupo_economico.nome}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status Contratual:</span>
                    <span className="font-semibold text-emerald-700">
                      {isImplantada ? 'Implantada' : 'Em Implantação'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100">
                <span className="text-[11px] text-gray-400 leading-tight block">
                  * As chaves emitidas nesta empresa operam com escopo nos dados da filial #{integracao.empresa_id}.
                </span>
              </div>
            </div>

            {/* Cartão de Whitelist de IPs Autorizados */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <GlobeAltIcon className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700">
                    Whitelist de IPs Autorizados
                  </h2>
                </div>
                <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full border border-primary-200">
                  Segurança de Rede
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                Restrinja os IPs autorizados para consumir a API REST da sua empresa. Se deixado em branco, requisições autenticadas com chave válida serão aceitas a partir de qualquer endereço.
              </p>

              <form onSubmit={handleSaveIps}>
                <div>
                  <textarea
                    id="company_allowed_ips"
                    rows={2}
                    value={allowedIpsInput}
                    onChange={(e) => setAllowedIpsOverride(e.target.value)}
                    placeholder="Ex: 200.180.10.5, 177.10.0.0/16 (separados por vírgula ou em linhas distintas)"
                    className="w-full text-xs font-mono rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-2.5"
                  />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {hasIpsChanged
                      ? 'Alterações não salvas'
                      : 'Regras de IP sincronizadas'}
                  </span>
                  <button
                    type="submit"
                    disabled={!hasIpsChanged || updateIpsMutation.isPending}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-xl shadow-sm transition ${
                      hasIpsChanged && !updateIpsMutation.isPending
                        ? 'bg-primary-600 text-white hover:bg-primary-700 cursor-pointer'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {updateIpsMutation.isPending ? 'Salvando...' : 'Salvar IPs da Empresa'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Tabela de Chaves de API */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <KeyIcon className="w-5 h-5 text-primary-600" />
                  <h2 className="text-sm font-bold text-gray-900">
                    Chaves de API Ativas
                  </h2>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Credenciais de acesso para envio no cabeçalho HTTP <code className="font-mono font-semibold bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">X-API-Key: sst_live_...</code>
                </p>
              </div>
              <button
                onClick={handleOpenCreateKey}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl shadow-sm transition bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
              >
                <PlusIcon className="w-4 h-4" />
                Criar Nova Chave
              </button>
            </div>

            {credenciais.length === 0 ? (
              <div className="py-14 text-center">
                <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-3 text-gray-400 border border-gray-200">
                  <KeyIcon className="w-6 h-6" />
                </div>
                <p className="text-sm font-semibold text-gray-700">
                  Nenhuma chave de API emitida para esta empresa
                </p>
                <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
                  Clique no botão &quot;Criar Nova Chave&quot; para emitir uma credencial de acesso ao ERP ou sistema de folha.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200/80 text-xs">
                  <thead className="bg-gray-50/70 text-gray-600 font-semibold uppercase tracking-wider text-[11px]">
                    <tr>
                      <th className="py-3 px-4 text-left">Identificador / Nome</th>
                      <th className="py-3 px-4 text-left">Escopo / Origem</th>
                      <th className="py-3 px-4 text-left">Prefixo do Token</th>
                      <th className="py-3 px-4 text-left">Módulos Permitidos</th>
                      <th className="py-3 px-4 text-left">Status</th>
                      <th className="py-3 px-4 text-left">Uso & Criação</th>
                      <th className="py-3 px-4 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {credenciais.map((c) => {
                      const modulosNomes = (c.allowed_modulos || [])
                        .map((modChave) => {
                          const found = modulosDisponiveis.find((m) => m.chave === modChave);
                          return found?.nome || modChave;
                        })
                        .join(', ');

                      return (
                        <tr key={c.id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="py-3.5 px-4">
                            <span className="font-semibold text-gray-900 block">{c.nome}</span>
                            {c.allowed_ips && (
                              <span className="text-[11px] font-mono text-gray-500 mt-0.5 block truncate max-w-xs">
                                IPs: {c.allowed_ips}
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-4">
                            {c.is_compartilhada ? (
                              <div className="flex flex-col items-start gap-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                                  <ShareIcon className="w-3 h-3 text-indigo-600" />
                                  Compartilhada (Grupo)
                                </span>
                                {c.empresas_compartilhadas && c.empresas_compartilhadas.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedSharedCred(c)}
                                    className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline font-medium cursor-pointer"
                                  >
                                    Ver {c.empresas_compartilhadas.length} empresas
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                Exclusiva da Empresa
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 font-mono font-semibold text-gray-800">
                            {c.key_prefix ? `${c.key_prefix}...` : 'sst_live_...'}
                          </td>
                          <td className="py-3.5 px-4 text-gray-600 max-w-xs truncate" title={modulosNomes}>
                            {modulosNomes || 'Todos permitidos'}
                          </td>
                          <td className="py-3.5 px-4">
                            {c.enabled ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary-50 text-primary-700 border border-primary-200">
                                Ativa
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                Revogada
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-gray-500">
                            <div>Criada: {c.created_at ? formatDateTimeBR(c.created_at) : '-'}</div>
                            <div className="text-[11px] text-gray-400">
                              {c.last_used_at ? `Último uso: ${formatDateTimeBR(c.last_used_at)}` : 'Nunca utilizada'}
                            </div>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            {c.is_compartilhada ? (
                              <span
                                className="text-[11px] text-indigo-600 font-medium italic"
                                title="Chave configurada no escopo do Grupo Econômico"
                              >
                                Chave do Grupo
                              </span>
                            ) : c.enabled ? (
                              <button
                                onClick={() => setKeyToRevoke({ id: c.id, nome: c.nome })}
                                className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 font-semibold transition cursor-pointer"
                                title="Revogar chave de acesso"
                              >
                                <TrashIcon className="w-4 h-4" />
                                Revogar
                              </button>
                            ) : (
                              <span className="text-gray-400 text-[11px] italic">Inativa</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== TAB 2: TUTORIAIS & GUIAS DE INTEGRAÇÃO ==================== */}
      {activeTab === 'tutoriais' && (
        <div className="space-y-6">
          {/* Seletor de Linguagem */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <CodeBracketIcon className="w-5 h-5 text-primary-600" />
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700">
                  Linguagem de Exemplo
                </h3>
                <p className="text-xs text-gray-500">
                  Escolha o formato dos snippets de código abaixo para copiar e colar no seu projeto:
                </p>
              </div>
            </div>
            <div className="flex items-center bg-gray-100 p-1 rounded-xl">
              {(['curl', 'node', 'python'] as CodeLanguage[]).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setSelectedLanguage(lang)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                    selectedLanguage === lang
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {lang === 'curl' ? 'cURL (Bash)' : lang === 'node' ? 'Node.js (TypeScript)' : 'Python 3'}
                </button>
              ))}
            </div>
          </div>

          {/* Card explicativo sobre Grupos Econômicos e Chaves Compartilhadas */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-indigo-200 p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-indigo-100 rounded-xl text-indigo-700 shrink-0">
                <BuildingOffice2Icon className="w-5 h-5" />
              </div>
              <div className="text-xs text-indigo-950">
                <h4 className="font-bold text-sm mb-1 text-indigo-900">
                  Regras para Grupos Econômicos & Chaves Compartilhadas
                </h4>
                <p className="leading-relaxed text-indigo-900">
                  Quando uma chave de API for compartilhada por duas ou mais empresas pertencentes a um grupo econômico:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1.5 text-indigo-950 font-medium">
                  <li>
                    <strong>Operações de Escrita (POST, PUT, DELETE):</strong> O cabeçalho{' '}
                    <code className="bg-indigo-100 px-1.5 py-0.5 rounded font-mono font-bold text-indigo-900">
                      X-Empresa-ID: {integracao.empresa_id}
                    </code>{' '}
                    (ou o atributo <code className="bg-indigo-100 px-1 py-0.5 rounded font-mono font-bold text-indigo-900">empresa_id</code> no body JSON) é <strong>estritamente obrigatório</strong> para indicar a empresa alvo dos dados.
                  </li>
                  <li>
                    <strong>Operações de Leitura (GET):</strong> O cabeçalho{' '}
                    <code className="bg-indigo-100 px-1 py-0.5 rounded font-mono text-indigo-900">X-Empresa-ID</code> é <strong>opcional</strong>.
                    Caso não informado, o endpoint lista dados consolidados de todas as empresas do grupo autorizadas para a chave. Se informado, filtrará apenas a empresa especificada.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* TUTORIAL 1: Autenticação & Teste de Conexão */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-bold text-xs flex items-center justify-center">
                    1
                  </span>
                  <h3 className="text-base font-bold text-gray-900">
                    Autenticação e Teste de Conexão
                  </h3>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed max-w-3xl">
                  Toda chamada deve incluir o cabeçalho <code className="font-mono font-bold text-primary-700 bg-primary-50 px-1 py-0.5 rounded">X-API-Key: sst_live_...</code>. A rota de domínios consolidados é aberta a qualquer chave válida e serve perfeitamente para testar a comunicação.
                </p>
              </div>
              <button
                onClick={() => handleCopySnippet('t1', snippets.dominios[selectedLanguage])}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200/80 transition cursor-pointer"
              >
                {copiedSnippetId === 't1' ? (
                  <>
                    <CheckIcon className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700">Copiado!</span>
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    <span>Copiar Snippet</span>
                  </>
                )}
              </button>
            </div>

            <div className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs overflow-x-auto border border-slate-800">
              <pre className="text-slate-200 whitespace-pre">
                {snippets.dominios[selectedLanguage]}
              </pre>
            </div>
          </div>

          {/* TUTORIAL 2: Sincronização de Colaboradores */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-bold text-xs flex items-center justify-center">
                    2
                  </span>
                  <h3 className="text-base font-bold text-gray-900">
                    Sincronização de Colaboradores (Admissão & Atualização)
                  </h3>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed max-w-3xl">
                  Ao criar ou atualizar colaboradores via integração, o sistema marca automaticamente <code className="font-mono text-primary-700 font-bold bg-primary-50 px-1 py-0.5 rounded">origem: &quot;INTEGRACAO&quot;</code> no cadastro do colaborador. Você pode também enviar o objeto <code className="font-mono text-primary-700 font-bold bg-primary-50 px-1 py-0.5 rounded">actor</code> para registrar quem operou no seu ERP.
                </p>
              </div>
              <button
                onClick={() => handleCopySnippet('t2', snippets.colaboradores[selectedLanguage])}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200/80 transition cursor-pointer"
              >
                {copiedSnippetId === 't2' ? (
                  <>
                    <CheckIcon className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700">Copiado!</span>
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    <span>Copiar Snippet</span>
                  </>
                )}
              </button>
            </div>

            <div className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs overflow-x-auto border border-slate-800">
              <pre className="text-slate-200 whitespace-pre">
                {snippets.colaboradores[selectedLanguage]}
              </pre>
            </div>
          </div>

          {/* TUTORIAL 3: Agendamento com Link no WhatsApp */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 font-bold text-xs flex items-center justify-center">
                    3
                  </span>
                  <h3 className="text-base font-bold text-gray-900">
                    Solicitar Agendamento com Escolha de Clínica via Link no WhatsApp
                  </h3>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-primary-100 text-primary-700">
                    Novo
                  </span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed max-w-3xl">
                  Ao acionar a rota <code className="font-mono text-primary-700 font-bold bg-primary-50 px-1 py-0.5 rounded">POST {apiBaseUrl}/agendamentos/solicitar-link-whatsapp</code>, o sistema cria o agendamento no status <em>Pendente Escolha</em> e dispara uma notificação interativa com link seguro para o colaborador escolher clínica, data e horário diretamente no WhatsApp.
                </p>
              </div>
              <button
                onClick={() => handleCopySnippet('t3', snippets.agendamento[selectedLanguage])}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200/80 transition cursor-pointer"
              >
                {copiedSnippetId === 't3' ? (
                  <>
                    <CheckIcon className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700">Copiado!</span>
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    <span>Copiar Snippet</span>
                  </>
                )}
              </button>
            </div>

            <div className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs overflow-x-auto border border-slate-800 mb-4">
              <pre className="text-slate-200 whitespace-pre">
                {snippets.agendamento[selectedLanguage]}
              </pre>
            </div>

            <div className="bg-primary-50/50 rounded-xl p-3 border border-primary-200 text-xs text-gray-700">
              <p className="font-semibold text-primary-900 mb-1 flex items-center gap-1.5">
                <ChatBubbleLeftRightIcon className="w-4 h-4 text-primary-600" />
                Estrutura de Resposta de Sucesso:
              </p>
              <pre className="font-mono text-[11px] text-gray-800 bg-white p-2.5 rounded-lg border border-primary-200 overflow-x-auto">
{`{
  "success": true,
  "agendamento_id": 98450,
  "pedido_id": 34120,
  "codigo_pedido": "PED-2026-98450",
  "colaborador": {
    "id": 124703,
    "nome": "Fernando Henrique de Souza",
    "cpf": "12345678901",
    "telefone": "11999998888"
  },
  "tipo_exame": "Periódico",
  "token_escolha_clinica": "a6b24e89-9831-4fa3-9fba-3714c330c6fa",
  "link_agendamento": "${apiHost}/escolha-clinica/a6b24e89-9831-4fa3-9fba-3714c330c6fa",
  "whatsapp": {
    "enviado": true,
    "telefone": "11999998888",
    "status": "enviado"
  }
}`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* ==================== TAB 3: AMBIENTES DA PLATAFORMA ==================== */}
      {activeTab === 'ambientes' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6">
            <h3 className="text-base font-bold text-gray-900 mb-2">
              Diretrizes de Ambientes (Staging e Produção)
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed mb-6">
              Cada ambiente possui sua própria base de dados e URL correspondente. O Swagger de cada ambiente aponta para a sua respectiva API, conforme configurado nas variáveis de ambiente.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Card Staging */}
              <div className={`p-5 rounded-2xl border-2 transition ${isStaging ? 'border-primary-500 bg-primary-50/40 relative' : 'border-gray-200/80 bg-white'}`}>
                {isStaging && (
                  <span className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary-600 text-white uppercase tracking-wider">
                    Ativo
                  </span>
                )}
                <h4 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <ServerStackIcon className="w-5 h-5 text-primary-600" />
                  Ambiente Staging (Homologação)
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  Ambiente de testes oficial para homologação de ERPs e sistemas parceiros.
                </p>

                <div className="bg-white p-3 rounded-xl border border-gray-200 font-mono text-xs text-primary-800 mb-4 break-all">
                  https://api.staging.sstexames.com.br/api/v1
                </div>

                <ul className="text-xs text-gray-700 space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-primary-600 shrink-0" />
                    <span>Disparos de mensagens em modo seguro</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-primary-600 shrink-0" />
                    <span>Não gera cobranças reais em fatura</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-primary-600 shrink-0" />
                    <span>Swagger staging: <code className="bg-gray-100 px-1 rounded font-mono">https://api.staging.sstexames.com.br/api/docs</code></span>
                  </li>
                </ul>
              </div>

              {/* Card Produção */}
              <div className={`p-5 rounded-2xl border-2 transition ${isProd ? 'border-primary-500 bg-primary-50/40 relative' : 'border-gray-200/80 bg-white'}`}>
                {isProd && (
                  <span className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary-600 text-white uppercase tracking-wider">
                    Ativo
                  </span>
                )}
                <h4 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5 text-gray-600" />
                  Ambiente Produção
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  Ambiente oficial para chamadas automatizadas após validação e homologação.
                </p>

                <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 font-mono text-xs text-gray-800 mb-4 break-all">
                  https://api.sstexames.com.br/api/v1
                </div>

                <ul className="text-xs text-gray-700 space-y-2">
                  <li className="flex items-center gap-2 text-rose-700">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0 text-rose-600" />
                    <span>Notificações reais enviadas aos colaboradores</span>
                  </li>
                  <li className="flex items-center gap-2 text-rose-700">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0 text-rose-600" />
                    <span>Lançamentos financeiros e faturamento de exames</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-gray-500 shrink-0" />
                    <span>Swagger prod: <code className="bg-gray-100 px-1 rounded font-mono">https://api.sstexames.com.br/api/docs</code></span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Checklist de Homologação */}
            <div className="border-t border-gray-100 pt-5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3">
                Checklist Recomendado Antes de Ir para Produção:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
                  <span className="font-semibold text-gray-900 block mb-1">1. Teste de Conexão</span>
                  Execute chamadas em <code className="bg-gray-200 px-1 rounded">/api/v1/dominios</code> utilizando sua chave de staging.
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
                  <span className="font-semibold text-gray-900 block mb-1">2. Sincronização Piloto</span>
                  Cadastre ou atualize um colaborador de teste validando o preenchimento de CPF e cargo.
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
                  <span className="font-semibold text-gray-900 block mb-1">3. Agendamento WhatsApp</span>
                  Dispare a rota de agendamento e verifique o link interativo recebido no WhatsApp de homologação.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nova Chave de API */}
      <Modal
        isOpen={isCreateKeyModalOpen}
        onClose={() => {
          setIsCreateKeyModalOpen(false);
          setKeyCreatedSecret(null);
        }}
        title={keyCreatedSecret ? 'Chave de API Gerada com Sucesso' : 'Criar Nova Chave de API'}
        size="lg"
      >
        {keyCreatedSecret ? (
          <div className="py-2">
            <div className="p-4 rounded-xl border border-primary-200 bg-primary-50/70 mb-4 flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-gray-800 leading-relaxed">
                <p className="font-bold mb-1 text-primary-900">Atenção: Copie a sua chave secreta agora!</p>
                <p>
                  Por motivos rigorosos de segurança e criptografia, este token <strong>não será exibido novamente</strong>. Se você perder esta chave, terá que revogá-la e gerar uma nova credencial.
                </p>
              </div>
            </div>

            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Token de API (Secret):
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={keyCreatedSecret}
                className="w-full font-mono text-xs p-2.5 rounded-xl border border-gray-300 bg-gray-50 text-gray-900 select-all"
              />
              <button
                type="button"
                onClick={handleCopySecret}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold rounded-xl shadow-sm transition ${
                  keyCopied
                    ? 'bg-emerald-600 text-white'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {keyCopied ? (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    Copiar
                  </>
                )}
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setIsCreateKeyModalOpen(false);
                  setKeyCreatedSecret(null);
                }}
                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition"
              >
                Concluir e Fechar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleCreateKeySubmit} className="space-y-4 py-2">
            <div>
              <label htmlFor="key_name" className="block text-xs font-semibold text-gray-700 mb-1">
                Nome da Integração <span className="text-red-500">*</span>
              </label>
              <input
                id="key_name"
                type="text"
                required
                value={newKeyForm.nome}
                onChange={(e) => setNewKeyForm({ ...newKeyForm, nome: e.target.value })}
                placeholder="Ex: ERP Senior - Módulo Folha / Integração RH"
                className="w-full text-xs rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-2.5"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Identificador para sua equipe reconhecer quem está consumindo este token.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Módulos Permitidos para esta Chave <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-200/80 rounded-xl p-3 bg-gray-50/50 space-y-2 max-h-48 overflow-y-auto">
                {modulosDisponiveis.map((m) => {
                  const isChecked = newKeyForm.allowed_modulos.includes(m.chave);
                  const isContracted = m.contratado;
                  return (
                    <label
                      key={m.chave}
                      className={`flex items-start gap-2.5 p-2 rounded-lg transition ${
                        isContracted
                          ? 'hover:bg-white cursor-pointer'
                          : 'opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={!isContracted}
                        checked={isChecked}
                        onChange={() => handleToggleModulo(m.chave)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                      />
                      <div className="flex-1 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-800">{m.nome}</span>
                          {!isContracted && (
                            <span className="text-[10px] text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded font-medium">
                              Não Contratado
                            </span>
                          )}
                        </div>
                        {m.descricao && (
                          <p className="text-[11px] text-gray-500 mt-0.5">{m.descricao}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Nota: A API de Domínios é aberta a qualquer chave válida e não depende de módulos contratuais.
              </p>
            </div>

            <div>
              <label htmlFor="key_allowed_ips" className="block text-xs font-semibold text-gray-700 mb-1">
                Restrição de IP específica para esta Chave (Opcional)
              </label>
              <input
                id="key_allowed_ips"
                type="text"
                value={newKeyForm.allowed_ips}
                onChange={(e) => setNewKeyForm({ ...newKeyForm, allowed_ips: e.target.value })}
                placeholder="Ex: 177.10.20.30, 201.50.0.0/16"
                className="w-full text-xs font-mono rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-2.5"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Se omitido, aplicam-se apenas os IPs permitidos gerais da empresa.
              </p>
            </div>

            <div>
              <label htmlFor="key_expires_at" className="block text-xs font-semibold text-gray-700 mb-1">
                Data de Expiração (Opcional)
              </label>
              <input
                id="key_expires_at"
                type="date"
                value={newKeyForm.expires_at}
                onChange={(e) => setNewKeyForm({ ...newKeyForm, expires_at: e.target.value })}
                className="w-full text-xs rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-2.5"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Deixe em branco para que a chave não expire automaticamente.
              </p>
            </div>

            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsCreateKeyModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createKeyMutation.isPending}
                className="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition shadow-sm disabled:opacity-50"
              >
                {createKeyMutation.isPending ? 'Gerando...' : 'Gerar Chave de API'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal: Confirmar Revogação */}
      <Modal
        isOpen={!!keyToRevoke}
        onClose={() => setKeyToRevoke(null)}
        title="Revogar Chave de API"
        size="sm"
      >
        <div className="py-2 text-xs text-gray-600">
          <p className="mb-3">
            Tem certeza de que deseja revogar a chave de API{' '}
            <strong className="text-gray-900">&quot;{keyToRevoke?.nome}&quot;</strong>?
          </p>
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-[11px] mb-4">
            <p className="font-semibold mb-0.5">Efeito Imediato</p>
            Qualquer sistema externo que utilize esta chave perderá o acesso instantaneamente. Esta ação é irreversível.
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setKeyToRevoke(null)}
              className="px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={revokeKeyMutation.isPending}
              onClick={() => keyToRevoke && revokeKeyMutation.mutate(keyToRevoke.id)}
              className="px-3.5 py-2 text-xs font-semibold text-white bg-rose-600 rounded-xl hover:bg-rose-700 transition shadow-sm disabled:opacity-50"
            >
              {revokeKeyMutation.isPending ? 'Revogando...' : 'Confirmar Revogação'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal: Detalhes do Compartilhamento */}
      <Modal
        isOpen={!!selectedSharedCred}
        onClose={() => setSelectedSharedCred(null)}
        title={`Empresas Compartilhadas — ${selectedSharedCred?.nome || ''}`}
        size="md"
      >
        <div className="space-y-4">
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-xs text-indigo-900">
            <p className="font-semibold mb-1">Chave no Escopo do Grupo Econômico</p>
            <p>
              Esta credencial possui acesso concedido a todas as empresas vinculadas ao grupo.
              Em operações de escrita (<code className="bg-indigo-100 px-1 py-0.5 rounded font-mono font-bold text-indigo-950">POST, PUT, DELETE</code>),
              o envio do cabeçalho <code className="bg-indigo-100 px-1 py-0.5 rounded font-mono font-bold text-indigo-950">X-Empresa-ID</code>{' '}
              é estritamente obrigatório. Em consultas (<code className="bg-indigo-100 px-1 py-0.5 rounded font-mono font-bold text-indigo-950">GET</code>), ele é opcional.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Empresas com Acesso ({selectedSharedCred?.empresas_compartilhadas?.length || 0})
            </h4>
            <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-xl">
              {selectedSharedCred?.empresas_compartilhadas?.map((emp) => (
                <div key={emp.id} className="p-2.5 flex items-center justify-between text-xs hover:bg-gray-50">
                  <span className="font-medium text-gray-900">{emp.nome}</span>
                  <span className="font-mono text-gray-500 text-[11px]">{formatCNPJ(emp.cnpj)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200 flex justify-end">
            <button
              type="button"
              onClick={() => setSelectedSharedCred(null)}
              className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition cursor-pointer"
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}
